import { useState, useEffect } from "react";
// Temporarily removed drag-and-drop imports until packages are installed
// import { DndProvider, useDrag, useDrop } from "react-dnd";
// import { HTML5Backend } from "react-dnd-html5-backend";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { GovernanceSubmission } from "./GovernanceSubmission";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Bell,
  MessageCircle,
  Clock,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Target,
  Briefcase,
  BarChart3,
  GripVertical,
  Plus,
  Send,
  Eye,
  ThumbsUp,
  ThumbsDown,
  TrendingUp
} from "lucide-react";

interface BoardGovernanceProps {
  onNavigateBack: () => void;
}

interface BoardItem {
  id: string;
  title: string;
  description: string;
  priority: number;
  status: "Under Review" | "Approved" | "Rejected" | "Deferred" | "Information Required";
  type: "Investment Proposal" | "Strategic Initiative" | "Risk Assessment" | "Policy Change" | "Budget Approval";
  submittedBy: string;
  submittedDate: string;
  financialImpact: number;
  riskLevel: "Low" | "Medium" | "High" | "Critical";
  comments: Comment[];
  decisionLog: DecisionEntry[];
}

interface Comment {
  id: string;
  author: string;
  content: string;
  timestamp: string;
  type: "comment" | "question" | "recommendation";
}

interface DecisionEntry {
  id: string;
  decision: string;
  rationale: string;
  decidedBy: string;
  timestamp: string;
  impact: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "success" | "urgent";
  timestamp: string;
  read: boolean;
  boardType: "commercial" | "operational" | "both";
}

// Mock data for Commercial Board
const commercialBoardItems: BoardItem[] = [
  {
    id: "CB-001",
    title: "Q1 2025 Capital Investment Plan",
    description: "Strategic capital allocation for technology infrastructure and market expansion initiatives",
    priority: 1,
    status: "Under Review",
    type: "Investment Proposal",
    submittedBy: "Sarah Johnson, CFO",
    submittedDate: "2024-12-10",
    financialImpact: 2500000,
    riskLevel: "Medium",
    comments: [
      {
        id: "c1",
        author: "Michael Chen, CEO",
        content: "The ROI projections look promising. However, I'd like to see more detailed risk mitigation strategies for the technology infrastructure component.",
        timestamp: "2024-12-12T10:30:00Z",
        type: "comment"
      },
      {
        id: "c2",
        author: "Lisa Wang, Board Member",
        content: "What's the expected timeline for market expansion? Do we have competitive analysis data?",
        timestamp: "2024-12-12T14:20:00Z",
        type: "question"
      }
    ],
    decisionLog: []
  },
  {
    id: "CB-002",
    title: "Strategic Partnership with TechCorp",
    description: "Joint venture proposal for expanding into European markets with established technology partner",
    priority: 2,
    status: "Information Required",
    type: "Strategic Initiative",
    submittedBy: "Robert Davis, VP Strategy",
    submittedDate: "2024-12-08",
    financialImpact: 1800000,
    riskLevel: "High",
    comments: [
      {
        id: "c3",
        author: "Sarah Johnson, CFO",
        content: "Need more clarity on the revenue sharing model and exit clauses.",
        timestamp: "2024-12-11T09:15:00Z",
        type: "recommendation"
      }
    ],
    decisionLog: []
  },
  {
    id: "CB-003",
    title: "Dividend Policy Review",
    description: "Annual review of dividend distribution policy considering current market conditions and growth investments",
    priority: 3,
    status: "Approved",
    type: "Policy Change",
    submittedBy: "Sarah Johnson, CFO",
    submittedDate: "2024-12-05",
    financialImpact: 5000000,
    riskLevel: "Low",
    comments: [],
    decisionLog: [
      {
        id: "d1",
        decision: "Approved with modifications",
        rationale: "Maintain current dividend rate but implement quarterly review process",
        decidedBy: "Commercial Board",
        timestamp: "2024-12-11T16:00:00Z",
        impact: "Ensures shareholder satisfaction while maintaining flexibility for growth investments"
      }
    ]
  }
];

// Mock data for Operational Board
const operationalBoardItems: BoardItem[] = [
  {
    id: "OB-001",
    title: "Supply Chain Resilience Initiative",
    description: "Implementation of redundant supply chains and vendor diversification to mitigate disruption risks",
    priority: 1,
    status: "Under Review",
    type: "Risk Assessment",
    submittedBy: "Amanda Rodriguez, COO",
    submittedDate: "2024-12-12",
    financialImpact: 750000,
    riskLevel: "Critical",
    comments: [
      {
        id: "c4",
        author: "David Wilson, Operations Director",
        content: "This is crucial given recent supply chain disruptions. Recommend fast-track approval.",
        timestamp: "2024-12-13T08:45:00Z",
        type: "recommendation"
      }
    ],
    decisionLog: []
  },
  {
    id: "OB-002",
    title: "Quality Management System Upgrade",
    description: "ISO 9001:2015 compliance upgrade and implementation of automated quality monitoring systems",
    priority: 2,
    status: "Approved",
    type: "Strategic Initiative",
    submittedBy: "Jennifer Kim, Quality Director",
    submittedDate: "2024-12-09",
    financialImpact: 450000,
    riskLevel: "Low",
    comments: [],
    decisionLog: [
      {
        id: "d2",
        decision: "Approved unanimously",
        rationale: "Essential for maintaining competitive advantage and regulatory compliance",
        decidedBy: "Operational Board",
        timestamp: "2024-12-12T14:30:00Z",
        impact: "Improved quality metrics and reduced compliance risk"
      }
    ]
  }
];

const mockNotifications: Notification[] = [
  {
    id: "n1",
    title: "Urgent Review Required",
    message: "Supply Chain Resilience Initiative requires immediate board attention due to critical risk level",
    type: "urgent",
    timestamp: "2024-12-15T09:30:00Z",
    read: false,
    boardType: "operational"
  },
  {
    id: "n2",
    title: "Decision Logged",
    message: "Dividend Policy Review has been approved with modifications",
    type: "success",
    timestamp: "2024-12-15T08:15:00Z",
    read: false,
    boardType: "commercial"
  },
  {
    id: "n3",
    title: "Information Request",
    message: "Strategic Partnership proposal requires additional documentation",
    type: "warning",
    timestamp: "2024-12-15T07:45:00Z",
    read: true,
    boardType: "commercial"
  }
];

// Simplified Board Item Component (without drag-and-drop)
function BoardItemCard({ item, onViewDetails }: { 
  item: BoardItem; 
  onViewDetails: (item: BoardItem) => void;
}) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Under Review": return "bg-blue-100 text-blue-800 border-blue-200";
      case "Approved": return "bg-green-100 text-green-800 border-green-200";
      case "Rejected": return "bg-red-100 text-red-800 border-red-200";
      case "Deferred": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Information Required": return "bg-orange-100 text-orange-800 border-orange-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "Critical": return "bg-red-100 text-red-800 border-red-200";
      case "High": return "bg-orange-100 text-orange-800 border-orange-200";
      case "Medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="p-4 border rounded-lg bg-card hover:shadow-md transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">#{item.priority}</span>
            <Badge variant="outline" className="text-xs">{item.id}</Badge>
          </div>
        </div>
        <Badge variant="outline" className={`${getStatusColor(item.status)} text-xs`}>
          {item.status}
        </Badge>
      </div>
      
      <h4 className="font-medium mb-2">{item.title}</h4>
      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
      
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <span className="text-muted-foreground">
            ${item.financialImpact.toLocaleString()}
          </span>
          <Badge variant="outline" className={`${getRiskColor(item.riskLevel)} text-xs`}>
            {item.riskLevel} Risk
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {item.comments.length > 0 && (
            <span className="flex items-center gap-1 text-muted-foreground">
              <MessageCircle className="h-3 w-3" />
              {item.comments.length}
            </span>
          )}
          <span className="text-muted-foreground">
            {new Date(item.submittedDate).toLocaleDateString()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetails(item)}
          >
            <MessageCircle className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BoardGovernance({ onNavigateBack }: BoardGovernanceProps) {
  const [activeTab, setActiveTab] = useState("commercial");
  const [commercialItems, setCommercialItems] = useState(commercialBoardItems);
  const [operationalItems, setOperationalItems] = useState(operationalBoardItems);
  const [notifications, setNotifications] = useState(mockNotifications);
  const [selectedItem, setSelectedItem] = useState<BoardItem | null>(null);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState<"comment" | "question" | "recommendation">("comment");
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);

  // Real-time notification simulation
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate new notifications
      if (Math.random() > 0.9) { // 10% chance every 5 seconds
        const newNotification: Notification = {
          id: `n${Date.now()}`,
          title: "Status Update",
          message: "A board item has been updated and requires your attention",
          type: "info",
          timestamp: new Date().toISOString(),
          read: false,
          boardType: Math.random() > 0.5 ? "commercial" : "operational"
        };
        
        setNotifications(prev => [newNotification, ...prev]);
        toast.info("New board notification", {
          description: newNotification.message
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleAddComment = () => {
    if (!selectedItem || !newComment.trim()) return;

    const comment: Comment = {
      id: `c${Date.now()}`,
      author: "Current User", // In a real app, this would be from auth context
      content: newComment,
      timestamp: new Date().toISOString(),
      type: commentType
    };

    // Update the item in the appropriate list
    if (selectedItem.id.startsWith("CB-")) {
      setCommercialItems(prev =>
        prev.map(item =>
          item.id === selectedItem.id
            ? { ...item, comments: [...item.comments, comment] }
            : item
        )
      );
    } else {
      setOperationalItems(prev =>
        prev.map(item =>
          item.id === selectedItem.id
            ? { ...item, comments: [...item.comments, comment] }
            : item
        )
      );
    }

    // Update selected item
    setSelectedItem(prev => prev ? { ...prev, comments: [...prev.comments, comment] } : null);
    setNewComment("");
    
    toast.success("Comment added", {
      description: "Your comment has been added to the board item"
    });
  };

  const handleStatusChange = (itemId: string, newStatus: BoardItem["status"]) => {
    const updateItem = (items: BoardItem[]) =>
      items.map(item =>
        item.id === itemId ? { ...item, status: newStatus } : item
      );

    if (itemId.startsWith("CB-")) {
      setCommercialItems(updateItem);
    } else {
      setOperationalItems(updateItem);
    }

    toast.success("Status updated", {
      description: `Item status changed to ${newStatus}`
    });
  };

  const markNotificationAsRead = (notificationId: string) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
  };

  const handleNewSubmission = (submissionData: any) => {
    // Convert submission to BoardItem format
    const newBoardItem: BoardItem = {
      id: submissionData.id,
      title: submissionData.title,
      description: submissionData.businessJustification.substring(0, 200) + "...",
      priority: submissionData.board === "commercial" ? commercialItems.length + 1 : operationalItems.length + 1,
      status: "Under Review",
      type: submissionData.submissionType as BoardItem["type"],
      submittedBy: submissionData.submittedBy,
      submittedDate: submissionData.submittedDate,
      financialImpact: submissionData.estimatedBudget,
      riskLevel: submissionData.riskLevel,
      comments: [],
      decisionLog: []
    };

    // Add to appropriate board
    if (submissionData.board === "commercial") {
      setCommercialItems(prev => [...prev, newBoardItem]);
    } else {
      setOperationalItems(prev => [...prev, newBoardItem]);
    }

    // Add live notification
    const newNotification: Notification = {
      id: `n${Date.now()}`,
      title: "New Submission",
      message: `${submissionData.title} has been added to ${submissionData.board} board`,
      type: "info",
      timestamp: new Date().toISOString(),
      read: false,
      boardType: submissionData.board
    };
    
    setNotifications(prev => [newNotification, ...prev]);

    // Show success toast
    toast.success(`Submission successfully sent to ${submissionData.board} Board for review.`, {
      description: "The item has been added to the Priority Board and board members have been notified."
    });

    // Switch to the appropriate tab
    setActiveTab(submissionData.board);
  };

  const unreadNotifications = notifications.filter(n => !n.read);
  const currentBoardNotifications = notifications.filter(n => 
    n.boardType === activeTab || n.boardType === "both"
  );

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={onNavigateBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl">Board Governance</h1>
              <p className="text-muted-foreground">
                Strategic oversight and decision management
              </p>
            </div>
          </div>
          
          {/* Real-time Notifications */}
          <div className="flex items-center gap-3">
            {unreadNotifications.length > 0 && (
              <div className="relative">
                <Bell className="h-6 w-6 text-primary animate-pulse" />
                <Badge 
                  variant="destructive" 
                  className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                >
                  {unreadNotifications.length}
                </Badge>
              </div>
            )}
            <Button 
              className="flex items-center gap-2"
              onClick={() => setIsSubmissionOpen(true)}
            >
              <Plus className="h-4 w-4" />
              New Submission
            </Button>
          </div>
        </div>

        {/* Real-time Notifications Panel */}
        {currentBoardNotifications.length > 0 && (
          <Card className="border-l-4 border-l-primary bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Bell className="h-5 w-5" />
                Live Updates
                <Badge variant="secondary">
                  {currentBoardNotifications.filter(n => !n.read).length} new
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {currentBoardNotifications.slice(0, 3).map((notification) => (
                  <div
                    key={notification.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                      !notification.read ? "bg-muted/50 border-primary/20" : "bg-background"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {notification.type === "urgent" && <AlertTriangle className="h-4 w-4 text-destructive" />}
                      {notification.type === "success" && <CheckCircle className="h-4 w-4 text-green-600" />}
                      {notification.type === "warning" && <AlertTriangle className="h-4 w-4 text-yellow-600" />}
                      {notification.type === "info" && <Bell className="h-4 w-4 text-blue-600" />}
                      <div>
                        <p className="font-medium text-sm">{notification.title}</p>
                        <p className="text-xs text-muted-foreground">{notification.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {new Date(notification.timestamp).toLocaleTimeString()}
                      </span>
                      {!notification.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markNotificationAsRead(notification.id)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Board Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="commercial" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Commercial Board
            </TabsTrigger>
            <TabsTrigger value="operational" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Operational Board
            </TabsTrigger>
          </TabsList>

          {/* Commercial Board */}
          <TabsContent value="commercial" className="space-y-6">
            {/* Commercial Dashboard Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Strategic Initiatives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">8</div>
                  <p className="text-xs text-muted-foreground">Active proposals</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Capital Allocation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">$12.5M</div>
                  <p className="text-xs text-muted-foreground">Under review</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    ROI Potential
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">24%</div>
                  <p className="text-xs text-muted-foreground">Avg projected ROI</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Decision Velocity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">7.2</div>
                  <p className="text-xs text-muted-foreground">Days avg</p>
                </CardContent>
              </Card>
            </div>

            {/* Commercial Priority Board */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GripVertical className="h-5 w-5" />
                  Priority Board - Commercial Governance
                  <Badge variant="secondary">Note: Drag-and-drop disabled temporarily</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {commercialItems.map((item) => (
                    <BoardItemCard
                      key={item.id}
                      item={item}
                      onViewDetails={setSelectedItem}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Operational Board */}
          <TabsContent value="operational" className="space-y-6">
            {/* Operational Dashboard Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Process Improvements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">12</div>
                  <p className="text-xs text-muted-foreground">Under review</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Risk Mitigation
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">3</div>
                  <p className="text-xs text-muted-foreground">Critical items</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    Efficiency Gains
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">18%</div>
                  <p className="text-xs text-muted-foreground">Projected improvement</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Compliance Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">94%</div>
                  <p className="text-xs text-muted-foreground">Overall compliance</p>
                </CardContent>
              </Card>
            </div>

            {/* Operational Priority Board */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GripVertical className="h-5 w-5" />
                  Priority Board - Operational Excellence
                  <Badge variant="secondary">Note: Drag-and-drop disabled temporarily</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {operationalItems.map((item) => (
                    <BoardItemCard
                      key={item.id}
                      item={item}
                      onViewDetails={setSelectedItem}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Board Item Details Dialog */}
        <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedItem?.title}</DialogTitle>
            </DialogHeader>
            
            {selectedItem && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Item Details */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Description</Label>
                    <p className="text-sm">{selectedItem.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Financial Impact</Label>
                      <p className="font-medium">${selectedItem.financialImpact.toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Risk Level</Label>
                      <Badge variant="outline" className="w-fit">{selectedItem.riskLevel}</Badge>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm text-muted-foreground">Submitted By</Label>
                    <p className="text-sm">{selectedItem.submittedBy}</p>
                  </div>

                  {/* Status Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(selectedItem.id, "Approved")}
                      className="flex items-center gap-1"
                    >
                      <ThumbsUp className="h-3 w-3" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(selectedItem.id, "Rejected")}
                      className="flex items-center gap-1"
                    >
                      <ThumbsDown className="h-3 w-3" />
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStatusChange(selectedItem.id, "Deferred")}
                    >
                      Defer
                    </Button>
                  </div>
                </div>

                {/* Comments and Decision Log */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Board Discussion</Label>
                    <ScrollArea className="h-48 mt-2 border rounded-lg p-3">
                      <div className="space-y-3">
                        {selectedItem.comments.map((comment) => (
                          <div key={comment.id} className="border-b pb-2 last:border-b-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium">{comment.author}</span>
                              <Badge variant="outline" className="text-xs">
                                {comment.type}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{comment.content}</p>
                            <span className="text-xs text-muted-foreground">
                              {new Date(comment.timestamp).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Label className="text-sm">Add Comment</Label>
                    <div className="flex items-center gap-2 mb-2">
                      <Button
                        variant={commentType === "comment" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCommentType("comment")}
                      >
                        Comment
                      </Button>
                      <Button
                        variant={commentType === "question" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCommentType("question")}
                      >
                        Question
                      </Button>
                      <Button
                        variant={commentType === "recommendation" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCommentType("recommendation")}
                      >
                        Recommendation
                      </Button>
                    </div>
                    <Textarea
                      placeholder="Enter your comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="flex items-center gap-2"
                    >
                      <Send className="h-3 w-3" />
                      Post Comment
                    </Button>
                  </div>

                  {/* Decision Log */}
                  {selectedItem.decisionLog.length > 0 && (
                    <div>
                      <Label className="text-sm font-medium">Decision History</Label>
                      <div className="mt-2 space-y-2">
                        {selectedItem.decisionLog.map((decision) => (
                          <div key={decision.id} className="border rounded-lg p-3 bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{decision.decision}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(decision.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{decision.rationale}</p>
                            <p className="text-xs text-muted-foreground">
                              Decided by: {decision.decidedBy}
                            </p>
                            <p className="text-xs">Impact: {decision.impact}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Governance Submission Modal */}
      <GovernanceSubmission
        isOpen={isSubmissionOpen}
        onClose={() => setIsSubmissionOpen(false)}
        onSubmit={handleNewSubmission}
      />
    </div>
  );
}